import React, { useState, useEffect } from "react";
import {
  Package,
  ClipboardList,
  BarChart3,
  Shield,
  Wrench,
  Monitor,
  Milk,
  Pill,
  Droplets,
  Users,
  Hammer,
  CheckSquare,
  CalendarDays,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Wallet,
  Baby,
  CalendarCheck,
  UserPlus,
  Newspaper,
  GraduationCap,
  FileText,
  Archive,
  Bus,
  ChefHat,
} from "lucide-react";

const menuItems = [
  {
    group: "Finances",
    items: [
      { id: "treasury", label: "Trésorerie", icon: Wallet, adminOnly: true },
      {
        id: "financial",
        label: "Dashboard financier",
        icon: TrendingUp,
        adminOnly: true,
      },
    ],
  },
  {
    group: "Crèche",
    items: [
      { id: "creche", label: "Places & CA", icon: Baby, module: "creche" },
      { id: "diapers", label: "Couches", icon: Package, module: "diapers" },
      { id: "meals", label: "Repas", icon: ChefHat, module: "creche" },
    ],
  },
  {
    group: "RH",
    items: [
      {
        id: "hr-dashboard",
        label: "Dashboard RH",
        icon: BarChart3,
        module: "hr_dashboard",
      },
      { id: "hr", label: "Employés", icon: Users, module: "hr" },
      {
        id: "hr-dossiers",
        label: "Dossiers RH",
        icon: Archive,
        module: "hr_dossiers",
      },
      {
        id: "hr-transport-collection",
        label: "Collecte transport",
        icon: Bus,
        module: "hr_transport_collection",
      },
      {
        id: "hr-document-templates",
        label: "Modèles de documents",
        icon: FileText,
        module: "hr",
      },
      {
        id: "recruitment",
        label: "Recrutement",
        icon: UserPlus,
        module: "recruitment",
      },
      {
        id: "timesheets",
        label: "Planning",
        icon: CalendarCheck,
        module: "timesheets",
      },
    ],
  },
  {
    group: "Gestion",
    items: [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: BarChart3,
        module: "stocks",
      },
      {
        id: "orders",
        label: "Commandes",
        icon: ClipboardList,
        module: "stocks",
      },
      { id: "stocks", label: "Stocks", icon: Package, module: "stocks" },
    ],
  },
  {
    group: "Traçabilités",
    items: [
      {
        id: "milk",
        label: "Traçabilité lait",
        icon: Milk,
        module: "traceability",
      },
      {
        id: "medications",
        label: "Traçabilité médicaments",
        icon: Pill,
        module: "traceability",
      },
      {
        id: "water",
        label: "Traçabilité eau",
        icon: Droplets,
        module: "traceability",
      },
    ],
  },
  {
    group: "Infrastructure",
    items: [
      {
        id: "equipment",
        label: "Équipements",
        icon: Monitor,
        module: "equipment",
      },
      {
        id: "maintenance",
        label: "Maintenance",
        icon: Wrench,
        module: "equipment",
      },
      { id: "workOrders", label: "Travaux", icon: Hammer, module: "equipment" },
    ],
  },
  {
    group: "Outils",
    items: [
      { id: "tasks", label: "Tâches", icon: CheckSquare, module: "tasks" },
      {
        id: "calendar",
        label: "Calendrier",
        icon: CalendarDays,
        module: "calendar",
      },
      {
        id: "communication",
        label: "Communication",
        icon: Newspaper,
        module: "communication",
      },
      { id: "admin", label: "Administration", icon: Shield, adminOnly: true },
    ],
  },
];

const hasModuleAccess = (userModules, userRole, module) => {
  if (userRole === "admin") return true;
  if (!userModules) return false;
  const modules = Array.isArray(userModules) ? userModules : [];
  return modules.includes(module);
};

const Sidebar = ({
  currentView,
  onNavigate,
  userRole,
  userModules,
  collapsed,
  onToggleCollapsed,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  // Accordion — tous les groupes ouverts par défaut
  const getActiveGroup = (view) =>
    menuItems.find((g) => g.items.some((i) => i.id === view))?.group;
  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    menuItems.forEach((g) => {
      init[g.group] = true;
    });
    return init;
  });

  // Ouvre automatiquement le groupe lors d'une navigation externe
  useEffect(() => {
    const active = getActiveGroup(currentView);
    if (active) setOpenGroups((prev) => ({ ...prev, [active]: true }));
  }, [currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavigate = (viewId) => {
    onNavigate(viewId);
    setMobileOpen(false);
  };

  const toggleGroup = (groupName) => {
    setOpenGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const adminItem =
    userRole === "admin"
      ? menuItems.flatMap((g) => g.items).find((i) => i.id === "admin")
      : null;

  // ── Desktop sidebar (inchangé) ──────────────────────────────────────────────
  const desktopContent = (
    <nav className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 py-4 overflow-y-auto">
        {menuItems.map((group) => {
          const visibleItems = group.items.filter((item) => {
            if (item.id === "admin") return false;
            if (item.adminOnly && userRole !== "admin") return false;
            if (
              item.module &&
              !hasModuleAccess(userModules, userRole, item.module)
            )
              return false;
            return true;
          });
          if (visibleItems.length === 0) return null;
          return (
            <div key={group.group} className="mb-4">
              {!collapsed && (
                <div className="px-4 mb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {group.group}
                </div>
              )}
              {visibleItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigate(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary border-r-3 border-primary"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    } ${collapsed ? "justify-center px-2" : ""}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon
                      size={20}
                      className={isActive ? "text-primary" : "text-gray-400"}
                    />
                    {!collapsed && (
                      <span className="truncate">{item.label}</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="border-t border-gray-200 flex-shrink-0">
        {adminItem &&
          (() => {
            const Icon = adminItem.icon;
            const isActive = currentView === adminItem.id;
            return (
              <button
                onClick={() => handleNavigate(adminItem.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                } ${collapsed ? "justify-center px-2" : ""}`}
                title={collapsed ? adminItem.label : undefined}
              >
                <Icon
                  size={20}
                  className={isActive ? "text-primary" : "text-gray-400"}
                />
                {!collapsed && (
                  <span className="truncate">{adminItem.label}</span>
                )}
              </button>
            );
          })()}
        <div className="p-2">
          <button
            onClick={onToggleCollapsed}
            className="w-full flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition"
            title={collapsed ? "Agrandir le menu" : "Réduire le menu"}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
      </div>
    </nav>
  );

  // ── Mobile drawer avec accordion ────────────────────────────────────────────
  const mobileContent = (
    <nav className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto">
        {menuItems.map((group) => {
          const visibleItems = group.items.filter((item) => {
            if (item.id === "admin") return false;
            if (item.adminOnly && userRole !== "admin") return false;
            if (
              item.module &&
              !hasModuleAccess(userModules, userRole, item.module)
            )
              return false;
            return true;
          });
          if (visibleItems.length === 0) return null;
          const isOpen = openGroups[group.group];
          const hasActive = visibleItems.some((i) => i.id === currentView);
          return (
            <div
              key={group.group}
              className="border-b border-gray-100 last:border-b-0"
            >
              {/* En-tête groupe cliquable */}
              <button
                onClick={() => toggleGroup(group.group)}
                className={`w-full flex items-center justify-between px-4 py-3 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  hasActive
                    ? "text-primary bg-primary/5"
                    : "text-gray-400 hover:bg-gray-50"
                }`}
              >
                <span>{group.group}</span>
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              {/* Items */}
              {isOpen && (
                <div>
                  {visibleItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentView === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavigate(item.id)}
                        className={`w-full flex items-center gap-3 pl-6 pr-4 py-3 text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-primary/10 text-primary border-l-2 border-primary"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                      >
                        <Icon
                          size={18}
                          className={
                            isActive ? "text-primary" : "text-gray-400"
                          }
                        />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Administration épinglée en bas */}
      {adminItem &&
        (() => {
          const Icon = adminItem.icon;
          const isActive = currentView === adminItem.id;
          return (
            <div
              className="border-t-2 border-gray-200 flex-shrink-0"
              style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
              <button
                onClick={() => handleNavigate(adminItem.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <Icon
                  size={18}
                  className={isActive ? "text-primary" : "text-gray-400"}
                />
                <span>{adminItem.label}</span>
              </button>
            </div>
          );
        })()}
    </nav>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-white shadow-md rounded-lg p-2 text-gray-600 hover:text-gray-900"
        aria-label="Ouvrir le menu"
      >
        <Menu size={22} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl flex flex-col transform transition-transform duration-200 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 flex-shrink-0">
          <span className="font-bold text-gray-900 text-lg">Suivi</span><span className="font-bold text-lg" style={{ color: '#0d9488' }}>Plus</span>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X size={20} />
          </button>
        </div>
        {mobileContent}
      </div>

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r border-gray-200 transition-all duration-200 ${
          collapsed ? "w-16" : "w-60"
        }`}
      >
        {!collapsed && (
          <div className="px-4 py-4 border-b border-gray-200">
            <span className="font-bold text-gray-900 text-lg">Suivi</span><span className="font-bold text-lg" style={{ color: '#0d9488' }}>Plus</span>
          </div>
        )}
        {collapsed && (
          <div className="px-2 py-4 border-b border-gray-200 flex justify-center">
            <Package size={24} className="text-primary" />
          </div>
        )}
        {desktopContent}
      </aside>
    </>
  );
};

export default Sidebar;
